import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, Eye, ExternalLink, MapPin } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { CityFilter } from '@/components/CityFilter'
import { spacing } from '@/theme'
import { useResponsive } from '@/hooks/useResponsive'
import { api } from '@/api/axios'
import { useEffect, useState } from 'react'

type Publication = {
  id: string
  title: string
  image: string | null
  link: string | null
  description: string
  startDate: string
  endDate: string
  city: string | null
}

export function PublicacionesScreen() {
  const navigate = useNavigate()
  const { isDesktop } = useResponsive()
  const [publications, setPublications] = useState<Publication[]>([])
  const [loading, setLoading] = useState(true)
  const [cityFilter, setCityFilter] = useState('')

  useEffect(() => {
    api.get('/publications/active').then((res) => {
      setPublications(res.data.data || [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const filtered = cityFilter ? publications.filter((p) => p.city === cityFilter) : publications
  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'linear-gradient(180deg, #020617 0%, #0f172a 30%, #5b21b6 70%, #0f172a 100%)',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/interacciones')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing[2],
              color: '#c4b5fd',
              marginBottom: spacing[3],
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <ArrowLeft size={18} />
            Volver
          </button>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              borderRadius: 24,
              padding: spacing[5],
              marginBottom: spacing[4],
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(76, 29, 149, 0.4) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3], position: 'relative', zIndex: 1 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)',
              }}>
                <Eye size={24} color="#fff" />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#ede9fe' }}>Publicaciones</h1>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#c4b5fd' }}>Informacion y novedades importantes para ti</p>
              </div>
            </div>
          </motion.div>

          <div style={{ marginBottom: spacing[4] }}>
            <CityFilter value={cityFilter} onChange={setCityFilter} label="Filtrar por ciudad" />
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ borderRadius: 20, overflow: 'hidden', background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                  <div style={{ height: 160, background: 'rgba(139, 92, 246, 0.05)' }} />
                  <div style={{ padding: spacing[3] }}>
                    <div style={{ width: '70%', height: 16, borderRadius: 8, background: 'rgba(139, 92, 246, 0.1)', marginBottom: 8 }} />
                    <div style={{ width: '100%', height: 10, borderRadius: 8, background: 'rgba(139, 92, 246, 0.05)', marginBottom: 4 }} />
                    <div style={{ width: '80%', height: 10, borderRadius: 8, background: 'rgba(139, 92, 246, 0.05)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: spacing[10], color: '#64748b' }}>
              <Eye size={48} style={{ margin: '0 auto', opacity: 0.3 }} />
              <p style={{ marginTop: spacing[2], fontSize: 15 }}>{cityFilter ? 'No hay publicaciones en esta ciudad' : 'No hay publicaciones activas'}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr', gap: spacing[3] }}>
              {filtered.map((pub, idx) => (
                <motion.div
                  key={pub.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={pub.link ? { y: -4, scale: 1.01 } : {}}
                  whileTap={pub.link ? { scale: 0.99 } : {}}
                  onClick={() => { if (pub.link) window.open(pub.link, '_blank', 'noopener,noreferrer') }}
                  style={{
                    borderRadius: 20,
                    overflow: 'hidden',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: pub.link ? 'pointer' : 'default',
                  }}
                >
                  {pub.image && (
                    <div style={{ position: 'relative', height: 180, overflow: 'hidden' }}>
                      <img
                        src={pub.image}
                        alt={pub.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(to top, rgba(15, 23, 42, 0.9) 0%, transparent 100%)',
                      }} />
                    </div>
                  )}
                  <div style={{ padding: spacing[3], display: 'flex', flexDirection: 'column',                     gap: '6px', flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.3 }}>
                      {pub.title}
                    </h3>
                    {pub.city && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#c4b5fd', fontWeight: 600 }}>
                        <MapPin size={13} /> {pub.city}
                      </span>
                    )}
                    <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.5, flex: 1 }}>
                      {pub.description}
                    </p>
                    {pub.link && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#8b5cf6', fontWeight: 600 }}>
                        <ExternalLink size={13} /> Ver más
                      </span>
                    )}
                    <div style={{ display: 'flex', gap: spacing[3], fontSize: 11, color: '#64748b', marginTop: spacing[1], paddingTop: spacing[2], borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={12} color="#8b5cf6" /> {formatDate(pub.startDate)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={12} color="#8b5cf6" /> {formatDate(pub.endDate)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}
